import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  Image, 
  Video, 
  Music, 
  X, 
  Play, 
  Pause,
  Volume2,
  Download,
  Eye,
  Trash2,
  FileText,
  Link,
  Check
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../../hooks/useToast';
import { useTenantContext } from '../../../context/TenantContext';
import { supabase } from '../../../lib/supabase';

interface MediaUploadProps {
  currentMediaUrl?: string;
  currentMediaType?: 'image' | 'video' | 'audio';
  onMediaChange: (url: string, type: 'image' | 'video' | 'audio') => void;
  onMediaRemove: () => void;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  currentMediaUrl,
  currentMediaType,
  onMediaChange,
  onMediaRemove
}) => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [urlInput, setUrlInput] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const maxFileSize = 50 * 1024 * 1024; // 50MB
  const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const allowedVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
  const allowedAudioTypes = ['audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a'];

  const getMediaIcon = (type: string) => {
    switch (type) {
      case 'image': return <Image className="w-5 h-5 text-blue-400" />;
      case 'video': return <Video className="w-5 h-5 text-purple-400" />;
      case 'audio': return <Music className="w-5 h-5 text-green-400" />;
      default: return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  const getFileType = (file: File): 'image' | 'video' | 'audio' | null => {
    if (allowedImageTypes.includes(file.type)) return 'image';
    if (allowedVideoTypes.includes(file.type)) return 'video';
    if (allowedAudioTypes.includes(file.type)) return 'audio';
    return null;
  };

  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize) {
      return 'Le fichier est trop volumineux (max 50MB)';
    }

    const fileType = getFileType(file);
    if (!fileType) {
      return 'Type de fichier non supporté';
    }

    return null;
  };

  const uploadFile = async (file: File) => {
    if (!tenant?.id) {
      toast({
        title: 'Erreur',
        description: 'Tenant non trouvé',
        variant: 'destructive'
      });
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      toast({
        title: 'Fichier invalide',
        description: validationError,
        variant: 'destructive'
      });
      return;
    }

    const fileType = getFileType(file);
    if (!fileType) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Générer un nom de fichier unique
      const fileExtension = file.name.split('.').pop();
      const fileName = `quiz-media/${tenant.id}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`;

      // Upload vers Supabase Storage
      const { data, error } = await supabase.storage
        .from('elearn-media')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Obtenir l'URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('elearn-media')
        .getPublicUrl(fileName);

      onMediaChange(publicUrl, fileType);
      
      toast({
        title: 'Upload réussi',
        description: `${fileType === 'image' ? 'Image' : fileType === 'video' ? 'Vidéo' : 'Audio'} uploadé avec succès`
      });

    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      toast({
        title: 'Erreur d\'upload',
        description: 'Impossible d\'uploader le fichier',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) return;

    // Détecter le type de média basé sur l'URL
    let mediaType: 'image' | 'video' | 'audio' = 'image';
    
    if (urlInput.match(/\.(mp4|webm|ogg|avi|mov)$/i)) {
      mediaType = 'video';
    } else if (urlInput.match(/\.(mp3|wav|ogg|m4a|aac)$/i)) {
      mediaType = 'audio';
    } else if (urlInput.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
      mediaType = 'image';
    }

    onMediaChange(urlInput.trim(), mediaType);
    setUrlInput('');
    
    toast({
      title: 'Média ajouté',
      description: 'URL du média ajoutée avec succès'
    });
  };

  const toggleAudioPlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const renderMediaPreview = () => {
    if (!currentMediaUrl || !currentMediaType) return null;

    switch (currentMediaType) {
      case 'image':
        return (
          <div className="relative group">
            <img
              src={currentMediaUrl}
              alt="Média de la question"
              className="w-full h-48 object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                toast({
                  title: 'Erreur de chargement',
                  description: 'Impossible de charger l\'image',
                  variant: 'destructive'
                });
              }}
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(currentMediaUrl, '_blank')}
                className="text-white hover:bg-white/20"
              >
                <Eye size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onMediaRemove}
                className="text-red-400 hover:bg-red-500/20"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="relative group">
            <video
              ref={videoRef}
              src={currentMediaUrl}
              controls
              className="w-full h-48 object-cover rounded-lg bg-black"
              onError={() => {
                toast({
                  title: 'Erreur de chargement',
                  description: 'Impossible de charger la vidéo',
                  variant: 'destructive'
                });
              }}
            >
              Votre navigateur ne supporte pas la lecture vidéo.
            </video>
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="sm"
                onClick={onMediaRemove}
                className="text-red-400 hover:bg-red-500/20 bg-black/50"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleAudioPlay}
                className="text-green-400 hover:bg-green-500/20"
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </Button>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Volume2 size={16} className="text-green-400" />
                  <span className="text-white text-sm">Fichier audio</span>
                </div>
                <audio
                  ref={audioRef}
                  src={currentMediaUrl}
                  onEnded={() => setIsPlaying(false)}
                  onError={() => {
                    toast({
                      title: 'Erreur de chargement',
                      description: 'Impossible de charger l\'audio',
                      variant: 'destructive'
                    });
                  }}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onMediaRemove}
                className="text-red-400 hover:bg-red-500/20"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {currentMediaUrl ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {getMediaIcon(currentMediaType || 'image')}
              <span className="text-white text-sm font-medium">
                Média attaché
              </span>
              <Badge variant="outline" className="border-blue-500/30 text-blue-400 bg-blue-500/10">
                {currentMediaType}
              </Badge>
            </div>
          </div>
          {renderMediaPreview()}
        </motion.div>
      ) : (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <Tabs defaultValue="upload" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-700/50">
                <TabsTrigger value="upload" className="data-[state=active]:bg-blue-600">
                  <Upload size={16} className="mr-2" />
                  Upload
                </TabsTrigger>
                <TabsTrigger value="url" className="data-[state=active]:bg-blue-600">
                  <Link size={16} className="mr-2" />
                  URL
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="space-y-4 mt-4">
                <div
                  className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploading ? (
                    <div className="space-y-3">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" />
                      <p className="text-gray-400">Upload en cours...</p>
                      <Progress value={uploadProgress} className="w-full" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mx-auto">
                        <Upload className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Cliquez pour uploader un fichier</p>
                        <p className="text-gray-400 text-sm">ou glissez-déposez ici</p>
                      </div>
                      <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Image size={12} />
                          <span>Images</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Video size={12} />
                          <span>Vidéos</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Music size={12} />
                          <span>Audio</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Max 50MB</p>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,audio/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isUploading}
                />
              </TabsContent>

              <TabsContent value="url" className="space-y-4 mt-4">
                <div className="space-y-3">
                  <Label className="text-gray-300">URL du média</Label>
                  <div className="flex space-x-2">
                    <Input
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      placeholder="https://example.com/media.jpg"
                      className="bg-gray-700 border-gray-600 text-white flex-1"
                    />
                    <Button
                      onClick={handleUrlSubmit}
                      disabled={!urlInput.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Check size={16} />
                    </Button>
                  </div>
                  <div className="text-xs text-gray-400">
                    Formats supportés : JPG, PNG, GIF, WebP, MP4, WebM, OGG, MP3, WAV, M4A
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export { MediaUpload };
